/**
 * The console's Room roster tab: the rooms this deployment declares.
 *
 * A declared room always exists. It is listed here whether or not anything is simulating it, it
 * keeps its name and its settings across a restart, and it is instantiated on its owning host by
 * the first human placed into it — so "idle" in this table is the normal, cheap state of a room
 * nobody is playing in, not a fault. Clicking a row opens its settings; that is the card's
 * "clicking a room opens its settings".
 *
 * The settings editor is `SettingsDialog`, the same component the live-room and host-defaults
 * editors use, with the name and the host handed to it as extra fields. There is no second copy
 * of the five knobs or of the validation behind them.
 *
 * WHERE A ROOM IS PINNED AND WHERE IT IS RUNNING ARE TWO COLUMNS, never one. "Opens on" is the
 * declaration — a host the operator reserved this room a place on, or *Auto*, which lets the
 * matchmaker choose by load on the join that wakes it. "Running on" is where a copy of it is at
 * this moment, which for an auto room is the only way to find out where it landed and for a
 * re-pinned one may not be the host it now names: a running room is never moved. Conflating the
 * two is what makes an operator distrust the page.
 */

import type { RoomSettings } from '@chompdome/shared/types';
import { useState } from 'react';
import type { RosterRow } from '../../../../server/src/trpc/routers/admin-rooms.js';
import { type RoomSettingsPatch, SettingsDialog } from '@/components/admin/settings-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';

/** An operator view, not a game loop; the same cadence as the rest of the console. */
const POLL_INTERVAL_MS = 5_000;

/**
 * The `<select>` value that means "no pin". The empty string, because an option's value is a
 * string and null is not one — it is turned back into null on the way to the mutation, which is
 * the wire's own spelling of auto.
 */
const AUTO = '';

/** Which editor is open. Only one is ever open at a time, as on the operations tab. */
type Editing =
  | { kind: 'create'; settings: RoomSettings }
  | { kind: 'edit'; room: RosterRow['room'] };

export interface RosterPanelProps {
  /**
   * The hosts a room may be pinned to, in registry order — `ProcessRow.id`, which is what the
   * roster stores and what placement matches on. Auto is offered above them and is the default:
   * a pin is a decision, not a thing to be handed out by whichever host sorted first.
   */
  hostIds: readonly string[];
  /** The policy a brand new declaration starts from, so the create dialog opens on real numbers. */
  defaultSettings: RoomSettings;
}

export function RosterPanel({ hostIds, defaultSettings }: RosterPanelProps) {
  const utils = trpc.useUtils();
  const roster = trpc.admin.rooms.list.useQuery(undefined, { refetchInterval: POLL_INTERVAL_MS });

  const [editing, setEditing] = useState<Editing | null>(null);
  const [removing, setRemoving] = useState<RosterRow | null>(null);
  /** The three fields `SettingsDialog` does not own, lifted here so both dialogs share them. */
  const [name, setName] = useState('');
  const [roomId, setRoomId] = useState('');
  const [workerId, setWorkerId] = useState<string>(AUTO);

  const refresh = async () => {
    await Promise.all([utils.admin.rooms.list.invalidate(), utils.admin.listRooms.invalidate()]);
  };
  const onSaved = async () => {
    setEditing(null);
    await refresh();
  };

  const create = trpc.admin.rooms.create.useMutation({ onSuccess: onSaved });
  const update = trpc.admin.rooms.update.useMutation({ onSuccess: onSaved });
  const remove = trpc.admin.rooms.remove.useMutation({
    onSuccess: async () => {
      setRemoving(null);
      await refresh();
    },
  });

  const openCreate = () => {
    setName('');
    setRoomId('');
    // Auto by default: pinning is what an operator does when they have a reason to, and a
    // deployment that has not decided should not be given one by the order of a dropdown.
    setWorkerId(AUTO);
    setEditing({ kind: 'create', settings: defaultSettings });
  };

  const openEdit = (row: RosterRow) => {
    setName(row.room.name);
    setRoomId(row.room.id);
    setWorkerId(row.room.workerId ?? AUTO);
    setEditing({ kind: 'edit', room: row.room });
  };

  const submit = (settings: RoomSettingsPatch, confirmUnsafe: boolean) => {
    if (editing === null) return;
    // Null, not undefined: the mutation reads an absent host as "leave the pin where it is", so
    // unpinning a room has to say so out loud.
    const pin = workerId === AUTO ? null : workerId;
    if (editing.kind === 'create') {
      create.mutate({
        id: roomId.trim(),
        name: name.trim(),
        workerId: pin,
        settings,
        confirmUnsafe,
      });
    } else {
      update.mutate({
        id: editing.room.id,
        name: name.trim(),
        workerId: pin,
        settings,
        confirmUnsafe,
      });
    }
  };

  const saving = editing?.kind === 'create' ? create : update;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Declared rooms always exist and keep their settings. A room starts running when the first
          player joins it and is shut down again once it has been empty for a while, so an idle room
          costs nothing.
        </p>
        {/* No longer gated on there being a host: an auto declaration names none, and a
            deployment whose hosts are all down still has a roster to edit. */}
        <Button size="sm" onClick={openCreate}>
          Declare a room
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          {roster.error && (
            <p className="pb-3 text-sm text-destructive" role="alert">
              {roster.error.message}
            </p>
          )}
          {roster.isLoading ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (roster.data ?? []).length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No rooms are declared. Players are placed into rooms opened on demand, which have no
              names and do not survive going empty.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Room id</th>
                    <th className="py-2 pr-3 font-medium">Opens on</th>
                    <th className="py-2 pr-3 font-medium">Running on</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 text-right font-medium">Humans / bots / max</th>
                    <th className="py-2 font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(roster.data ?? []).map((row) => (
                    <tr
                      key={row.room.id}
                      className="border-b border-border/60 last:border-0 hover:bg-muted/40"
                    >
                      <td className="py-2 pr-3 font-medium">
                        {/* The row itself is the "click a room to open its settings" target;
                            it is a button so the keyboard reaches it as well as the pointer. */}
                        <button
                          type="button"
                          className="cursor-pointer text-left font-semibold text-primary hover:underline"
                          onClick={() => openEdit(row)}
                        >
                          {row.room.name}
                        </button>
                      </td>
                      <td className="py-2 pr-3 break-all text-muted-foreground">{row.room.id}</td>
                      {/* The declaration: the host this room is pinned to, or Auto. Never where
                          it is at the moment — that is the next column, and they are allowed to
                          disagree. */}
                      <td className="py-2 pr-3 text-muted-foreground">
                        {row.room.workerId === null ? (
                          <span className="italic">Auto</span>
                        ) : (
                          row.room.workerId
                        )}
                        {!row.hostKnown && (
                          <span className="ml-1.5 text-destructive">(unknown host)</span>
                        )}
                      </td>
                      {/* Where a copy of it actually is. Read off the host that answered with
                          the room, so an auto room says where it landed and a room that was
                          re-pinned while running says where it still is. */}
                      <td className="py-2 pr-3 text-muted-foreground">{row.runningOn ?? '—'}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                            row.live === null
                              ? 'bg-muted text-muted-foreground'
                              : 'bg-primary/15 text-primary'
                          }`}
                        >
                          {row.live === null ? 'idle' : 'running'}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                        {row.live === null
                          ? `— / — / ${row.room.settings.maxPlayers}`
                          : `${row.live.humanCount} / ${row.live.botCount} / ${row.live.maxPlayers}`}
                      </td>
                      <td className="py-2">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(row)}
                            aria-label={`Edit ${row.room.name}`}
                          >
                            Settings
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setRemoving(row)}
                            aria-label={`Remove ${row.room.name} from the roster`}
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <SettingsDialog
          open
          title={editing.kind === 'create' ? 'Declare a room' : `Room ${editing.room.name}`}
          description={
            editing.kind === 'create'
              ? 'The room is created on its host the first time a player is placed into it, with these settings.'
              : 'These are the settings the room is started with, its world size included. A copy that is running right now keeps the settings it started with — change those from Rooms & Processes.'
          }
          settings={editing.kind === 'create' ? editing.settings : editing.room.settings}
          serverError={saving.error?.message ?? null}
          pending={saving.isPending}
          submitLabel={editing.kind === 'create' ? 'Declare room' : 'Save room'}
          onSubmit={submit}
          onClose={() => setEditing(null)}
          extraFields={
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <label htmlFor="roster-name" className="text-sm font-medium">
                  Name
                </label>
                <Input
                  id="roster-name"
                  value={name}
                  maxLength={48}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="roster-id" className="text-sm font-medium">
                  Room id
                </label>
                <Input
                  id="roster-id"
                  value={roomId}
                  maxLength={64}
                  // Fixed once declared: it is what a share link and a signed ticket address
                  // the room by, so re-identifying it would strand both.
                  disabled={editing.kind === 'edit'}
                  onChange={(event) => setRoomId(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="roster-host" className="text-sm font-medium">
                  Opens on
                </label>
                <select
                  id="roster-host"
                  value={workerId}
                  onChange={(event) => setWorkerId(event.target.value)}
                  className="h-9 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-1 text-sm"
                >
                  <option value={AUTO}>Auto (least loaded host)</option>
                  {/* The room's own host first even when it has been taken out of the fleet:
                      an operator has to be able to see the pin they are about to change. */}
                  {[...new Set([workerId, ...hostIds])].filter(Boolean).map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  A pinned room opens only on that host, and is unavailable while it is down. Auto
                  lets the matchmaker choose. Either way a room that is already running stays where
                  it is until it goes empty.
                </p>
              </div>
            </div>
          }
        />
      )}

      {removing && (
        <Dialog
          open
          title="Remove this room from the roster?"
          description={`${removing.room.name} stops being somewhere new players are sent. It ends no matches: a copy that is running keeps going with the people in it and is shut down when it next goes empty.`}
          onClose={() => setRemoving(null)}
        >
          {remove.error && (
            <p className="mb-3 text-sm text-destructive" role="alert">
              {remove.error.message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={remove.isPending}
              onClick={() => remove.mutate({ id: removing.room.id })}
            >
              {remove.isPending ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        </Dialog>
      )}
    </section>
  );
}
